import { createRouter, createWebHashHistory } from 'vue-router'

import DefaultLayout from '@/layouts/DefaultLayout'
import { isModuleEnabled } from '@/helpers/tenantModules'
import store from '../store'
import mainRoutes from '@/router/main'
import TokenService from '@/services/storage.service'
import {
  abortNavigationToLogin,
  isAuthSessionExpired,
  isSessionExpiredResponse,
  LOGIN_PATH,
  SessionExpiredError
} from '@/auth/session'
import { CHANGE_PASSWORD_PATH, hasTemporaryPasswordChangeSession } from '@/auth/temporaryPassword'
import { ensureValidAccessToken } from '@/auth/refresh'

const routes = [
  {
    path: '/',
    name: 'login',
    component: () => import('@/views/pages/Login')
  },
  {
    path: CHANGE_PASSWORD_PATH,
    name: 'change-password',
    component: () => import('@/views/pages/ChangePassword.vue')
  },
  {
    path: '/dashboard',
    name: 'home',
    component: DefaultLayout,
    children: [...mainRoutes]
  },
  {
    path: '/error',
    name: 'error',
    component: () => import('@/views/pages/error/View.vue')
  }
]

const router = createRouter({
  history: createWebHashHistory(process.env.BASE_URL),
  routes,
  scrollBehavior() {
    return { top: 0 }
  }
})

router.beforeEach(async (to, from, next) => {
  const isLoginRoute = to.path === LOGIN_PATH || to.name === 'login'
  const isChangePasswordRoute = to.path === CHANGE_PASSWORD_PATH || to.name === 'change-password'
  const isErrorRoute = to.path === '/error' || to.name === 'error'
  const isPublicRoute = isLoginRoute || isErrorRoute || isChangePasswordRoute

  if (isChangePasswordRoute) {
    if (!hasTemporaryPasswordChangeSession()) {
      next({ path: LOGIN_PATH, replace: true })
      return
    }

    next()
    return
  }

  if (isLoginRoute) {
    if (hasTemporaryPasswordChangeSession()) {
      next({ path: CHANGE_PASSWORD_PATH, replace: true })
      return
    }

    next()
    return
  }

  if (!TokenService.getToken() || isAuthSessionExpired()) {
    abortNavigationToLogin(next, to.fullPath)
    return
  }

  try {
    await ensureValidAccessToken()
  } catch (error) {
    abortNavigationToLogin(next, to.fullPath)
    return
  }

  let requiredRole = to.meta.requiredRole
  let requiredPermission = to.meta.requiredPermission

  const needsModuleCheck =
    requiredRole ||
    requiredPermission ||
    to.meta.moduleSlug ||
    to.matched.some((record) => record.meta.moduleSlug || record.meta.requiredRole)

  if (needsModuleCheck) {
    try {
      await store.dispatch('me', { requireFresh: true })

      if (!TokenService.getToken() || isAuthSessionExpired()) {
        abortNavigationToLogin(next, to.fullPath)
        return
      }

      const tenantModules = store.getters.tenantModules
      const availableModules = store.getters.availableModules

      if (Object.keys(tenantModules).length === 0 || availableModules.length === 0) {
        try {
          await store.dispatch('loadTenantModules')
        } catch (error) {
          console.error('[Router] Failed to load permissions/modules:', error)
        }
      }

      if (!TokenService.getToken() || isAuthSessionExpired()) {
        abortNavigationToLogin(next, to.fullPath)
        return
      }

      if (requiredRole) {
        let user = store.getters.getAdminRole
        let role = user.find((item) => requiredRole.includes(item.name))

        if (!role) {
          next('/dashboard')
          return
        }
      }

      if (requiredPermission) {
        const { hasPermission } = await import('@/helpers/permissions')
        if (!hasPermission(requiredPermission)) {
          next('/dashboard')
          return
        }
      }

      for (const record of to.matched) {
        const moduleSlug = record.meta.moduleSlug
        const isParent = record.meta.isParent
        const checkModuleConfig = record.meta.checkModuleConfig

        if (moduleSlug && !isParent) {
          if (!isModuleEnabled(moduleSlug)) {
            console.warn(`Access denied to route ${to.path} - missing module: ${moduleSlug} (route: ${record.path})`)
            next('/dashboard')
            return
          }
        }

        if (checkModuleConfig) {
          const { getTenantConfigValue } = await import('@/helpers/tenantModules')
          const configValue = getTenantConfigValue(checkModuleConfig, false)

          const isConfigEnabled =
            typeof configValue === 'boolean' ? configValue : Array.isArray(configValue) ? configValue.length > 0 : !!configValue

          if (!isConfigEnabled) {
            console.warn(`Access denied to route ${to.path} - config disabled: ${checkModuleConfig} (route: ${record.path})`)
            next('/dashboard')
            return
          }
        }
      }

      next()
      return
    } catch (e) {
      console.error('[Router] Auth or module check failed:', e)
      if (e instanceof SessionExpiredError || isSessionExpiredResponse(e) || !TokenService.getToken() || isAuthSessionExpired()) {
        abortNavigationToLogin(next, to.fullPath)
        return
      }
      next('/dashboard')
      return
    }
  }

  if (!isPublicRoute && (!TokenService.getToken() || isAuthSessionExpired())) {
    abortNavigationToLogin(next, to.fullPath)
    return
  }

  next()
})

export default router
